import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { db } from '../firebaseInit';
import { collection, query, getDocs, getDocsFromServer, getDocsFromCache, doc, getDoc } from 'firebase/firestore';
import * as firestorePaths from '../utils/firestorePaths';
import { getArtifactProjectId } from '../utils/firestorePaths';
import { normalizeEmailToDocId } from '../utils/emailNormalization';

const DataContext = createContext();

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};

export const DataProvider = ({ children }) => {
  // userTeam auto-switches when admin views another team via AuthContext
  const { user, claims, isAuthenticated, userTeam } = useAuth();
  const [templates, setTemplates] = useState({});
  const [templateVersion, setTemplateVersion] = useState(0);
  const [userDefaults, setUserDefaults] = useState(null);
  const [teamData, setTeamData] = useState(null);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [dataError, setDataError] = useState(null);

  const appId = getArtifactProjectId();

        
  // Calculate template permissions based on user role and template metadata
  const calculateTemplatePermissions = (templateData, templateId, userRole, userEmail, userTeamId) => {
    const creatorId = templateData?.metadata?.creatorId || templateData?.creatorId || templateData?.ownerId || templateData?.userId;
    const creatorTeam = templateData?.metadata?.creatorTeam || templateData?.creatorTeam || null;
    const isOwnTemplate = creatorId === userEmail;
    const isSameTeam = userTeamId && creatorTeam && userTeamId === creatorTeam;
    const isExampleScript = templateId === 'example-script';
        
    // Debug permission calculation in development
    if (process.env.NODE_ENV === 'development') {
      console.log('🔒 Permission calculation:', {
        templateId,
        userRole,
        userEmail,
        userTeamId,
        creatorId,
        creatorTeam,
        isOwnTemplate,
        isSameTeam,
        isExampleScript,
        templateData: templateData ? {
          hasMetadata: !!templateData.metadata,
          metadataKeys: templateData.metadata ? Object.keys(templateData.metadata) : [],
          topLevelKeys: Object.keys(templateData)
        } : 'no template data'
      });
    }
        
    let canEdit = false;
    let isReadOnly = true;
    
    // NOTE: All team templates are now editable, including example-script.
    // Each team has their own copy of example-script (no global templates).
    // See commit 98d41f0 "Template assignment architecture simplification".
        
    if (userRole === 'admin') {
      // Admins can edit all templates
      canEdit = true;
      isReadOnly = false;
    } else if (userRole === 'team_lead') {
      // Team leads can edit all templates from their team
      if (!userTeamId) {
        canEdit = isOwnTemplate;
      } else {
        // Team leads can edit templates if:
        // 1. They own the template (creatorId matches)
        // 2. Template is from their team (creatorTeam matches)
        // 3. Template has no creator info but is in their team's template collection (fallback)
        const isFromMyTeam = isOwnTemplate || isSameTeam || (!creatorId && !creatorTeam);
        canEdit = isFromMyTeam;
      }
      isReadOnly = !canEdit;
    } else {
      // Agents can only edit their own templates
      canEdit = isOwnTemplate;
      isReadOnly = !canEdit;
    }
        
    // Debug permission result in development
    if (process.env.NODE_ENV === 'development') {
      console.log('🔒 Permission result:', { templateId, canEdit, isReadOnly });
    }
        
    return { canEdit, isReadOnly };
  };

  const loadTemplates = useCallback(async () => {
    if (!isAuthenticated) {
      console.log('❌ Not authenticated, skipping template loading');
      return;
    }
        
    if (!claims) {
      console.log('❌ No claims available, skipping template loading');
      return;
    }

    // Cache-first helper: race getDocsFromCache against a 300ms timeout.
    // If IndexedDB isn't ready or has no data in 300ms, fall through to server.
    const fetchCacheFirst = async (q) => {
      let fromCache = false;
      let snapshot = null;
      try {
        snapshot = await Promise.race([
          getDocsFromCache(q),
          new Promise((_, reject) => setTimeout(() => reject(new Error('cache timeout')), 300))
        ]);
        if (snapshot && snapshot.docs.length > 0) {
          fromCache = true;
        } else {
          snapshot = null; // empty cache — prefer fresh server data
        }
      } catch { /* cache miss or timeout */ }

      if (!snapshot) {
        try {
          snapshot = await getDocsFromServer(q);
        } catch (serverErr) {
          console.warn('⚠️ Server fetch failed, trying cache fallback:', serverErr.message);
          snapshot = await getDocs(q);
        }
      }

      // Background server refresh when we served stale cache
      if (fromCache) {
        getDocsFromServer(q).catch(() => { /* non-critical */ });
      }

      return { snapshot, fromCache };
    };

    try {
      console.log('📚 Loading templates for user:', claims?.role, 'team:', userTeam);
      setIsLoadingData(true);
      setDataError(null);

      const templatesData = {};

      // Helper to process a doc snapshot into templatesData
      const processDocs = (snapshot, accessLevel, source, extra = {}) => {
        snapshot.forEach(docSnap => {
          const templateData = docSnap.data();
          const permissions = calculateTemplatePermissions(templateData, docSnap.id, claims?.role, user?.email, userTeam);
          const graphData = templateData?.data?.scriptGraph || templateData?.graph;
          if (!graphData) {
            console.warn(`⚠️ Template ${docSnap.id} has no graph data, skipping`);
            return;
          }
          if (templateData?.data?.scriptGraph && !templateData?.graph) {
            console.log(`🔄 Normalizing template ${docSnap.id}: data.scriptGraph → graph`);
          }
          templatesData[docSnap.id] = {
            id: docSnap.id,
            name: templateData?.metadata?.name || templateData?.name || `Template ${docSnap.id}`,
            graph: graphData,
            steps: templateData?.steps || [],
            prospectForm: (templateData?.prospectForm?.fields?.length > 0) ? templateData.prospectForm : null,
            accessLevel,
            source,
            metadata: templateData?.metadata || {},
            canEdit: permissions.canEdit,
            isReadOnly: permissions.isReadOnly,
            _dataVersion: '2.0',
            ...extra,
          };
        });
      };

      if (claims?.role === 'admin') {
        // ADMIN: load global templates + team templates in PARALLEL
        const globalPath = firestorePaths.getGlobalTemplatesPath(appId);
        const globalQuery = query(collection(db, globalPath));

        const teamPath = userTeam ? firestorePaths.getTemplatesPath(appId, userTeam) : null;
        const teamQuery = teamPath ? query(collection(db, teamPath)) : null;

        console.log('🔍 Admin detected - loading templates from team:', userTeam);

        const [globalResult, teamResult] = await Promise.all([
          fetchCacheFirst(globalQuery),
          teamQuery ? fetchCacheFirst(teamQuery) : Promise.resolve({ snapshot: null }),
        ]);

        processDocs(globalResult.snapshot, 'global', 'system');

        if (teamResult.snapshot) {
          console.log(`📁 Found ${teamResult.snapshot.size} templates in team:`, userTeam);
          processDocs(teamResult.snapshot, 'team', 'team', {
            teamId: userTeam,
            aiPersonalization: null,
          });
        } else {
          console.log('⚠️ Admin has no team to view, only system templates will be available');
        }
      } else if (userTeam) {
        // NON-ADMIN: Load only own team templates
        console.log('📚 Loading templates for team:', userTeam);
        const teamPath = firestorePaths.getTemplatesPath(appId, userTeam);
        const teamQuery = query(collection(db, teamPath));
        const { snapshot: teamSnapshot } = await fetchCacheFirst(teamQuery);

        teamSnapshot.forEach(docSnap => {
          const templateData = docSnap.data();
          const permissions = calculateTemplatePermissions(templateData, docSnap.id, claims?.role, user?.email, userTeam);
          const graphData = templateData?.data?.scriptGraph || templateData?.graph;
          if (!graphData) {
            console.warn(`⚠️ Template ${docSnap.id} has no graph data, skipping`);
            return;
          }
          if (templateData?.data?.scriptGraph && !templateData?.graph) {
            console.log(`🔄 Normalizing template ${docSnap.id}: data.scriptGraph → graph`);
          }
          templatesData[docSnap.id] = {
            id: docSnap.id,
            name: templateData?.metadata?.name || templateData?.name || `Template ${docSnap.id}`,
            graph: graphData,
            steps: templateData?.steps || [],
            prospectForm: (templateData?.prospectForm?.fields?.length > 0) ? templateData.prospectForm : null,
            accessLevel: 'team',
            source: 'team',
            teamId: userTeam,
            metadata: templateData?.metadata || {},
            aiPersonalization: templateData?.aiPersonalization || null,
            canEdit: permissions.canEdit,
            isReadOnly: permissions.isReadOnly,
            _dataVersion: '2.0',
          };
        });
      } else if (claims?.role !== 'admin') {
        console.log('⚠️ User has no team. Role:', claims?.role, 'userTeam:', userTeam);
      }

      setTemplates(templatesData);
      setTemplateVersion(v => v + 1);
      console.log('✅ Templates loaded:', Object.keys(templatesData).length, 'templates');
            
      // Debug logging for template structure
      Object.entries(templatesData).forEach(([id, template]) => {
        const hasGraph = !!(template?.graph?.nodes);
        const hasDataScriptGraph = !!(template?.data?.scriptGraph?.nodes);
        const templateName = template?.name || 'NO_NAME';
        const stepsCount = template?.steps?.length || 0;
        const hasProspectForm = !!template?.prospectForm;
        console.log(`📋 Template ${id}: name="${templateName}", hasGraph=${hasGraph}, hasDataScriptGraph=${hasDataScriptGraph}, stepsCount=${stepsCount}, hasProspectForm=${hasProspectForm}`);
        if (stepsCount > 0) {
          console.log(`   └─ Steps: ${template.steps.map(s => s.id).join(', ')}`);
        }
      });
    } catch (error) {
      console.error('❌ Error loading templates:', error);
      setDataError(error);
    } finally {
      setIsLoadingData(false);
    }
  }, [isAuthenticated, userTeam, claims?.role, user?.email, appId]);

  const loadUserDefaults = useCallback(async () => {
    if (!isAuthenticated || !user?.email || !claims?.teamId) return;

    try {
      console.log('👤 Loading user defaults');
      const emailDocId = normalizeEmailToDocId(user.email);
      const userPath = firestorePaths.getUserPath(appId, claims.teamId, emailDocId);
      const userDoc = await getDoc(doc(db, userPath));
            
      if (userDoc.exists()) {
        const userData = userDoc.data();
        setUserDefaults(userData);
        console.log('✅ User defaults loaded');
      } else {
        console.log('⚠️ No user defaults found');
        setUserDefaults({});
      }
    } catch (error) {
      console.error('❌ Error loading user defaults:', error);
      setDataError(error);
    }
  }, [isAuthenticated, user?.email, claims?.teamId, appId]);

  const loadTeamData = useCallback(async () => {
    if (!isAuthenticated || !userTeam) return;

    try {
      console.log('🏢 Loading team data for team:', userTeam);
      const teamPath = firestorePaths.getTeamPath(appId, userTeam);
      const teamDocRef = doc(db, teamPath);
      const teamSnapshot = await getDoc(teamDocRef);

      if (teamSnapshot.exists()) {
        const data = teamSnapshot.data();
        setTeamData(data);
        console.log('✅ Team data loaded:', {
          name: data.name,
          industryId: data.industryId || '(not set)',
          hasIndustryDescription: !!data.industryDescription,
        });
      } else {
        console.log('⚠️ No team document found for team:', userTeam);
        setTeamData(null);
      }
    } catch (error) {
      console.error('❌ Error loading team data:', error);
      // Don't set dataError here — team data is supplementary, not critical
    }
  }, [isAuthenticated, userTeam, appId]);

  // Load data when authentication is ready or when viewed team changes
  useEffect(() => {
    let isActive = true;

    if (isAuthenticated && claims && userTeam) {
      const loadAll = async () => {
        try {
          await Promise.all([loadTemplates(), loadUserDefaults(), loadTeamData()]);
        } catch (err) {
          // Only update state if this effect is still active (team hasn't changed)
          if (isActive) {
            console.error('Error loading data:', err);
          }
        }
      };
      loadAll();
    } else if (!isAuthenticated) {
      // Clear data when user logs out to prevent cross-user data leakage
      setTemplates({});
      setUserDefaults(null);
      setTeamData(null);
      setDataError(null);
    }

    return () => { isActive = false; };
  }, [isAuthenticated, claims, userTeam, loadTemplates, loadUserDefaults, loadTeamData]);

  const value = {
    templates,
    templateVersion,
    userDefaults,
    teamData,
    isLoadingData,
    dataError,
    refreshTemplates: loadTemplates,
    refreshUserDefaults: loadUserDefaults,
    refreshTeamData: loadTeamData,
  };

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
};
